/**
 * Branch Master — Custom form with Branch Details + Additional Information tabs
 * Table: BranchMaster
 */
import React, { useState, useEffect, useCallback, memo } from 'react';
import { Save, Grid3X3, XCircle, Plus } from 'lucide-react';
import ERPAdvancedGrid from '../../components/erp/ERPAdvancedGrid';
import DynamicFieldRenderer from '../../components/erp/DynamicFieldRenderer';
import { ERPAddressBlock, EMPTY_ADDR } from '../../components/erp/ERPAddressBlock';
import { GlobalizationBar, DEFAULT_GLOBALIZATION } from '../../components/erp/GlobalizationBar';
import { erpToast } from '../../components/erp/ERPToast';
import '../../styles/erp-theme.css';
import { API_BASE as API_URL, getToken} from '../../lib/api';

const enterNav = (e) => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') { e.preventDefault(); const f = e.target.closest('form'); if (!f) return; const els = Array.from(f.querySelectorAll('input:not([disabled]):not([readonly]),select:not([disabled])')); const i = els.indexOf(e.target); if (i >= 0 && i < els.length - 1) els[i + 1].focus(); } };
const F = memo(({ label, value, onChange, required, disabled, type = 'text' }) => (<div className="erp-form-row"><label className={`erp-form-label${required ? ' required' : ''}`}>{label}</label><div className="erp-form-control"><input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="erp-input" onKeyDown={enterNav} /></div></div>));
const Sel = memo(({ label, value, onChange, options, required, disabled }) => (<div className="erp-form-row"><label className={`erp-form-label${required ? ' required' : ''}`}>{label}</label><div className="erp-form-control"><select value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="erp-input erp-select" onKeyDown={enterNav}><option value="">--Select--</option>{options.map(o => typeof o === 'string' ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}</select></div></div>));

const empty = { ID: '', Unit: 'Branch', Name: '', Location: '', Branchid: '', Branchtype: '1', MarkUpValue: '0', MarkUpText: 'Purchase Price', MarkDownValue: '0', MarkDownText: 'MRP', RoundOff: 'Round', CustomerId: '', GSTIN: '', Createddate: '' };

const hbsToDate = (v) => { if (!v || isNaN(v)) return ''; const n = parseInt(v); const y = Math.floor(n / 416) + 1950; const r = n % 416; const m = Math.floor(r / 32); const d = r % 32; if (m < 1 || m > 12 || d < 1 || d > 31) return ''; return `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`; };
const dateToHbs = (s) => { if (!s) return ''; const [y, m, d] = s.split('-').map(Number); if (!y || !m || !d) return ''; return (y - 1950) * 416 + m * 32 + d; };

const BranchMasterScreen = ({ theme = 'light', onBack }) => {
  const [view, setView] = useState('grid');
  const [form, setForm] = useState(empty);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [tab, setTab] = useState('branch');
  const [mode, setMode] = useState('add');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dynValues, setDynValues] = useState({});
  const [addr, setAddr] = useState(EMPTY_ADDR);
  const [globalConfig, setGlobalConfig] = useState(DEFAULT_GLOBALIZATION || { language: 'en', currency: 'INR', numberFormat: 'en-IN', dateFormat: 'dd/MM/yyyy' });
  const handleAddrChange = useCallback((f, v) => setAddr(p => ({ ...p, [f]: v })), []);
  const handleAddrLoaded = useCallback((d) => setAddr(p => ({ ...p, ...d })), []);
  const token = getToken() || '';
  const userRole = (() => { try { return JSON.parse(localStorage.getItem('retailcliq_user') || '{}').role || ''; } catch { return ''; } })();
  const isSuperAdmin = userRole === 'SUPER_ADMIN' || userRole === 'admin' || userRole === 'ADMINSUPPORT' || userRole === 'LICENSOR';
  const isView = mode === 'view';
  const set = (n, v) => setForm(p => ({ ...p, [n]: v }));

  const loadData = useCallback(async () => {
    if (!token) return;
    try { const r = await fetch(`${API_URL}/api/foundation/branch?page=${page}&pageSize=${pageSize}`, { headers: { Authorization: `Bearer ${token}` } }); const j = await r.json(); if (j.success) { setData(j.data || []); setTotal(j.total || 0); } } catch {}
  }, [token, page, pageSize]);
  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    if (!form.Name?.trim()) { setError('Branch Name is required'); return; }
    setSaving(true); setError('');
    try {
      const method = mode === 'edit' ? 'PUT' : 'POST';
      const url = mode === 'edit' ? `${API_URL}/api/foundation/branch/${form.ID}` : `${API_URL}/api/foundation/branch`;
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(form) });
      const json = await res.json();
      if (json.success) {
        const recordId = mode === 'edit' ? form.ID : json.data?.ID;
        if (recordId) {
          const addrBody = { Addtype: 'BR', Addtitid: recordId, ...addr };
          if (addr.AddId) { await fetch(`${API_URL}/api/address-book/${addr.AddId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(addrBody) }); }
          else { await fetch(`${API_URL}/api/address-book`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(addrBody) }); }
        }
        erpToast.success(mode === 'edit' ? 'Updated!' : 'Saved!'); setForm(empty); setAddr(EMPTY_ADDR); setMode('add'); loadData();
      }
      else setError(json.error || 'Save failed');
    } catch { setError('Save failed'); }
    setSaving(false);
  };

  const handleVoid = async (row) => {
    if (!window.confirm(`Void branch "${row.Name}"?`)) return;
    await fetch(`${API_URL}/api/foundation/branch/${row.ID}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    erpToast.success('Voided'); loadData();
  };

  const tp = Math.ceil(total / pageSize) || 1;
  const TABS = [{ id: 'branch', label: 'Branch Details' }, { id: 'additional', label: 'Additional Information' }, { id: 'globalization', label: 'Globalization' }];

  const BRANCH_COLUMNS = [
    { field: 'ID', header: 'ID' },
    { field: 'Unit', header: 'Unit' },
    { field: 'Name', header: 'Name' },
    { field: 'Location', header: 'Location' },
    { field: 'GSTIN', header: 'GSTIN' },
    { field: 'Branchtype', header: 'Type' },
  ];

  if (view === 'grid') {
    return (
      <div className="erp-module">
        <ERPAdvancedGrid
          title="Branch Master"
          columns={BRANCH_COLUMNS}
          data={data}
          total={total}
          idField="ID"
          nameField="Name"
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          loading={false}
          onAddNew={() => { setView('form'); setMode('add'); setForm(empty); setAddr(EMPTY_ADDR); }}
          onEdit={(row, m) => { setForm({ ...empty, ...row }); setAddr(EMPTY_ADDR); setMode(m || 'edit'); setView('form'); }}
          onVoid={handleVoid}
          testId="branch-master"
          exportFileName="BranchMaster"
          showFilters={false}
          cellRenderer={(field, value) => {
            if (field === 'Location') return (value || '').substring(0, 30);
            return null;
          }}
        />
      </div>
    );
  }

  return (
    <div className="erp-module">
      <div className="erp-screen-header">
        {onBack ? (<button onClick={onBack} data-testid="screen-back-btn" style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",padding:"2px 0"}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 5-7 7 7 7"/></svg><span className="erp-screen-title" style={{color:"var(--erp-accent,#1856a7)"}}>Branch Master</span></button>) : (<span className="erp-screen-title">Branch Master</span>)}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 11 }}>
          {form.ID ? <span>ID: <strong style={{ fontFamily: 'var(--erp-font-mono)', color: 'var(--erp-primary)' }}>{form.ID}</strong></span>
          : <><span style={{ color: 'var(--erp-text-muted)' }}>Previous ID: <strong style={{ fontFamily: 'var(--erp-font-mono)' }}>{data[0]?.ID || '—'}</strong></span><span style={{ color: 'var(--erp-text-secondary)' }}>New ID: <strong style={{ color: 'var(--erp-warning)', fontFamily: 'var(--erp-font-mono)' }}>Auto-generated</strong></span></>}
        </div>
        <div className="erp-screen-actions">
          {!isView && <button className="erp-btn erp-btn-ghost" onClick={handleSave} disabled={saving}><Save size={13} /> {saving ? 'Saving...' : (mode === 'edit' ? 'Update' : 'Save')}</button>}
          <button className="erp-btn erp-btn-ghost" onClick={() => setView('grid')}><Grid3X3 size={13} /> Gridview</button>
          <button className="erp-btn erp-btn-ghost" onClick={() => { setForm(empty); setMode('add'); setError(''); setDynValues({}); setAddr(EMPTY_ADDR); }}><XCircle size={13} /> Clear</button>
          {isSuperAdmin && !isView && <button className="erp-btn erp-btn-primary" onClick={() => document.querySelector('[data-testid="add-dynamic-field-btn"]')?.click()}><Plus size={13} /> Add Field</button>}
        </div>
      </div>
      {error && <div className="erp-alert-error">{error}</div>}
      <form style={{ flex: 1, overflow: 'auto', padding: 'var(--erp-sp-3)' }} onSubmit={(e) => e.preventDefault()}>
        <div className="erp-form-card">
          <div className="erp-tabs">{TABS.map(t => <button key={t.id} className={`erp-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>)}</div>
          <div className="erp-form-section">
            {tab === 'branch' && (<>
              <div className="erp-form-2col">
                <div>
                  <F label="ID" value={form.ID} onChange={(v) => set('ID', v)} disabled />
                  <Sel label="Unit" value={form.Unit} onChange={(v) => set('Unit', v)} options={['Branch', 'Franchise', 'Warehouse', 'Head Office']} disabled={isView} />
                  <F label="Name" value={form.Name} onChange={(v) => set('Name', v)} required disabled={isView} />
                </div>
                <div>
                  <F label="SGST/CGST No" value={form.GSTIN} onChange={(v) => set('GSTIN', v)} required disabled={isView} />
                  <F label="GST Reg. Date" value={hbsToDate(form.Createddate)} onChange={(v) => set('Createddate', dateToHbs(v))} required type="date" disabled={isView} />
                  <div className="erp-form-row"><label className="erp-form-label">Default</label><div className="erp-form-control" style={{ gap: 12 }}><label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}><input type="radio" name="default" checked={form.Branchtype === '1'} onChange={() => set('Branchtype', '1')} disabled={isView} />Yes</label><label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}><input type="radio" name="default" checked={form.Branchtype !== '1'} onChange={() => set('Branchtype', '0')} disabled={isView} />No</label></div></div>
                  <F label="Customer" value={form.CustomerId} onChange={(v) => set('CustomerId', v)} disabled={isView} />
                </div>
              </div>
              <ERPAddressBlock addType="BR" recordId={form.ID} values={addr} onChange={handleAddrChange} onLoadAddress={handleAddrLoaded} readOnly={isView} token={token} />
            </>)}
            {tab === 'additional' && (
              <div className="erp-form-2col">
                <div>
                  <F label="Legal Entity ID" value={form.Branchid} onChange={(v) => set('Branchid', v)} disabled={isView} />
                  <F label="Mark Up %" value={form.MarkUpValue} onChange={(v) => set('MarkUpValue', v)} type="number" disabled={isView} />
                  <Sel label="Mark Up On" value={form.MarkUpText} onChange={(v) => set('MarkUpText', v)} options={['Purchase Price', 'MRP', 'Cost Price']} disabled={isView} />
                </div>
                <div>
                  <F label="Mark Down %" value={form.MarkDownValue} onChange={(v) => set('MarkDownValue', v)} type="number" disabled={isView} />
                  <Sel label="Mark Down On" value={form.MarkDownText} onChange={(v) => set('MarkDownText', v)} options={['MRP', 'Purchase Price']} disabled={isView} />
                  <Sel label="Round Off" value={form.RoundOff} onChange={(v) => set('RoundOff', v)} options={['Round', 'Floor', 'Ceil']} disabled={isView} />
                </div>
              </div>
            )}
            {tab === 'globalization' && (
              <div style={{ padding: 'var(--erp-sp-3)' }}>
                <GlobalizationBar config={globalConfig} onChange={setGlobalConfig} token={token} />
              </div>
            )}
          </div>
        </div>
        <DynamicFieldRenderer screenName="BranchMaster" recordId={form.ID} token={token} values={dynValues} onChange={(k, v) => setDynValues(p => ({ ...p, [k]: v }))} readOnly={isView} userRole={userRole} />
        <div className="erp-embedded-grid"><div className="erp-grid-wrapper">
          <table className="erp-grid erp-grid-header-fixed"><thead><tr><th style={{ width: 50 }}>Actions</th><th>ID</th><th>Unit</th><th>Name</th><th>Location</th><th>GSTIN</th></tr></thead></table>
          <div className="erp-grid-body-scroll"><table className="erp-grid"><tbody>{data.map(row => (
            <tr key={row.ID}><td style={{ width: 50 }}><ERPActionColumn row={{ ...row, CatId: row.ID }} onView={() => { setForm({ ...empty, ...row }); setAddr(EMPTY_ADDR); setMode('view'); }} onEdit={() => { setForm({ ...empty, ...row }); setAddr(EMPTY_ADDR); setMode('edit'); }} onVoid={() => handleVoid(row)} /></td>
            <td className="cell-mono">{row.ID}</td><td>{row.Unit}</td><td className="cell-bold">{row.Name}</td><td>{row.Location?.substring(0, 25)}</td><td>{row.GSTIN}</td></tr>
          ))}</tbody></table></div>
        </div></div>
      </form>
    </div>
  );
};

export default BranchMasterScreen;
